import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { Input, Autocomplete, AutocompleteItem } from "@heroui/react";
import { useEffect, useState } from "react";
import { Key } from "@react-types/shared";

export interface FieldFilterBarProps {
    searchValue?: string;
    onSearchChange: (value: string) => void;

    serviceProviderId?: string;
    onServiceProviderChange: (id: string | undefined) => void;
    serviceProviderOptions?: { key: string; label: string }[];

    growerId?: string;
    onGrowerChange: (id: string | undefined) => void;
    growerOptions?: { key: string; label: string }[];

    accountManagerId?: string;
    onAccountManagerChange?: (id: string | undefined) => void;
    accountManagerOptions?: { key: string; label: string }[];
}

export function FieldFilterBar({
    searchValue,
    onSearchChange,
    serviceProviderId,
    onServiceProviderChange,
    serviceProviderOptions = [],
    growerId,
    onGrowerChange,
    growerOptions = [],
    accountManagerId,
    onAccountManagerChange,
    accountManagerOptions = [],
}: FieldFilterBarProps) {
    const [debouncedSearch, setDebouncedSearch] = useState(searchValue ?? "");

    useEffect(() => {
        if (debouncedSearch === searchValue) return;
        const handler = setTimeout(() => {
            onSearchChange(debouncedSearch);
        }, 700);

        return () => clearTimeout(handler);
    }, [debouncedSearch, onSearchChange, searchValue]);

    const [serviceProviderKey, setServiceProviderKey] = useState<Key | null>(
        serviceProviderId || null,
    );
    const [growerKey, setGrowerKey] = useState<Key | null>(growerId || null);
    const [accountManagerKey, setAccountManagerKey] = useState<Key | null>(
        accountManagerId || null,
    );

    useEffect(() => {
        setServiceProviderKey(serviceProviderId || null);
    }, [serviceProviderId]);

    useEffect(() => {
        setGrowerKey(growerId || null);
    }, [growerId]);

    useEffect(() => {
        setAccountManagerKey(accountManagerId || null);
    }, [accountManagerId]);

    const handleServiceProviderChange = (key: Key | null) => {
        setServiceProviderKey(key);
        onServiceProviderChange(key ? key.toString() : undefined);
    };

    const handleGrowerChange = (key: Key | null) => {
        setGrowerKey(key);
        onGrowerChange(key ? key.toString() : undefined);
    };

    const handleAccountManagerChange = (key: Key | null) => {
        setAccountManagerKey(key);
        if (onAccountManagerChange) {
            onAccountManagerChange(key ? key.toString() : undefined);
        }
    };

    return (
        <div className="flex flex-row gap-2 items-center">
            <Input
                placeholder="Search..."
                size="sm"
                startContent={<FontAwesomeIcon icon={faSearch} />}
                classNames={{
                    base: "min-w-48",
                }}
                value={debouncedSearch}
                onValueChange={setDebouncedSearch}
                isClearable
            />
            <Autocomplete
                placeholder="Service Provider"
                size="sm"
                startContent={<FontAwesomeIcon icon={faSearch} />}
                classNames={{
                    base: "min-w-48",
                }}
                aria-label="Service Provider"
                isVirtualized
                selectedKey={serviceProviderKey}
                onSelectionChange={handleServiceProviderChange}
                allowsCustomValue={false}
            >
                {serviceProviderOptions.map((option) => (
                    <AutocompleteItem key={option.key}>
                        {option.label}
                    </AutocompleteItem>
                ))}
            </Autocomplete>
            <Autocomplete
                placeholder="Farm"
                size="sm"
                startContent={<FontAwesomeIcon icon={faSearch} />}
                classNames={{
                    base: "min-w-48",
                }}
                aria-label="Farm"
                isVirtualized
                selectedKey={growerKey}
                onSelectionChange={handleGrowerChange}
                allowsCustomValue={false}
            >
                {growerOptions.map((option) => (
                    <AutocompleteItem key={option.key}>
                        {option.label}
                    </AutocompleteItem>
                ))}
            </Autocomplete>
            {accountManagerOptions.length > 0 && onAccountManagerChange && (
                <Autocomplete
                    placeholder="Account Manager"
                    size="sm"
                    startContent={<FontAwesomeIcon icon={faSearch} />}
                    classNames={{
                        base: "min-w-56",
                    }}
                    aria-label="Account Manager"
                    isVirtualized
                    selectedKey={accountManagerKey}
                    onSelectionChange={handleAccountManagerChange}
                    allowsCustomValue={false}
                >
                    {accountManagerOptions.map((option) => (
                        <AutocompleteItem key={option.key}>
                            {option.label}
                        </AutocompleteItem>
                    ))}
                </Autocomplete>
            )}
        </div>
    );
}
